import React, { useState, useEffect, useRef } from 'react'
import { convert_name } from '../../../utils/conversion'

const MultipleSelectInput = React.forwardRef(
  (
    {
      label,
      name,
      options,
      required,
      error,
      onChange,
      value = [],
      placeholder,
      ...rest
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [filteredOptions, setFilteredOptions] = useState(options)
    const [selectedValues, setSelectedValues] = useState(
      Array.isArray(value) ? value : []
    )
    const dropdownRef = useRef(null)
    const inputRef = useRef(null)
    const errorClass = 'border-red-500'

    useEffect(() => {
      const filtered = options.filter((option) => {
        const unsignedOption = convert_name({
          name: option.label,
        }).toLowerCase()

        return unsignedOption.includes(
          convert_name({ name: searchTerm }).toLowerCase()
        )
      })
      setFilteredOptions(filtered.slice(0, 10))
    }, [searchTerm, options])

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false)
          setSearchTerm('')
        }
      }

      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [])

    useEffect(() => {
      const newValueString = JSON.stringify(Array.isArray(value) ? value : [])
      const currentValueString = JSON.stringify(selectedValues)

      if (newValueString !== currentValueString) {
        setSelectedValues(Array.isArray(value) ? value : [])
      }
    }, [value])

    const handleSelectChange = (newSelectedValues) => {
      setSelectedValues(newSelectedValues)
      if (onChange) {
        onChange({
          target: {
            name,
            value: newSelectedValues,
          },
        })
      }
    }

    const handleOptionClick = (optionValue) => {
      let newSelectedValues
      if (selectedValues.includes(optionValue)) {
        newSelectedValues = selectedValues.filter((v) => v !== optionValue)
      } else {
        newSelectedValues = [...selectedValues, optionValue]
      }
      handleSelectChange(newSelectedValues)
      setSearchTerm('')
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }

    const handleRemoveOption = (optionValue, e) => {
      e.stopPropagation()
      const newSelectedValues = selectedValues.filter((v) => v !== optionValue)
      handleSelectChange(newSelectedValues)
    }

    const handleInputFocus = () => {
      setIsOpen(true)
    }

    const handleKeyDown = (e) => {
      if (
        e.key === 'Backspace' &&
        searchTerm === '' &&
        selectedValues.length > 0
      ) {
        const newSelectedValues = [...selectedValues]
        newSelectedValues.pop()
        handleSelectChange(newSelectedValues)
      }
    }
    const selectedOptions = selectedValues
      .map((val) => options.find((option) => option.value == val))
      .filter(Boolean)

    return (
      <section>
        <label
          htmlFor={name}
          className="mb-1 block text-sm font-bold text-black"
        >
          {label} {required && <span className="text-red-accent">*</span>}
        </label>
        <div className="relative" ref={dropdownRef}>
          <div
            className={`focus:shadow-outline flex min-h-[2.8rem] w-full cursor-pointer appearance-none items-center overflow-hidden rounded-md border border-gray-400 bg-white px-2 leading-tight text-gray-700 shadow focus:outline-none ${error && errorClass}`}
            onClick={() => inputRef.current?.focus()}
          >
            {selectedOptions.map((option) => (
              <div
                key={option.value}
                className="mx-1 flex items-center rounded bg-[#D9D9D9] px-2 py-1 text-xs hover:bg-gray-200"
              >
                <span className="max-h-[2.8em] overflow-hidden text-ellipsis break-words">
                  {option.label}
                </span>
                <button
                  type="button"
                  className="ml-1 text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={(e) => handleRemoveOption(option.value, e)}
                >
                  ×
                </button>
              </div>
            ))}
            <input
              ref={inputRef}
              type="text"
              className="min-w-[50px] flex-grow border-none px-2 py-1 focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
            />
          </div>
          {isOpen && (
            <ul className="absolute z-10 my-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white shadow-lg">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <li
                    key={index}
                    className={`m-3 flex cursor-pointer items-center justify-between rounded-sm px-4 py-1 text-xs hover:bg-light-gray ${
                      selectedValues.includes(option.value)
                        ? 'bg-gray-secondary text-white'
                        : ''
                    }`}
                    onClick={() => handleOptionClick(option.value)}
                  >
                    <span className="mr-3 truncate">{option.label}</span>
                    {selectedValues.includes(option.value) && (
                      <span className="font-bold text-white">✓</span>
                    )}
                  </li>
                ))
              ) : (
                <li className="px-4 py-2 italic text-gray-500">
                  Không tìm thấy kết quả nào.
                </li>
              )}
            </ul>
          )}
        </div>
        {error && <p className="mt-1 text-xs text-red-500">{error.message}</p>}
      </section>
    )
  }
)

export default MultipleSelectInput
